/**
 * Segments Tab Component
 * Shows customer segmentation with auto-tagging, segment cards, and customer list
 */
import { Users, Zap, Crown, Heart, Star, Sparkles, TrendingUp, AlertCircle, Clock, AlertTriangle, Moon, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Segment icon mapping
const SEGMENT_ICONS = {
  champions: Crown,
  loyal_customers: Heart,
  potential_loyalists: Star,
  new_customers: Sparkles,
  promising: TrendingUp,
  needs_attention: AlertCircle,
  about_to_sleep: Clock,
  at_risk: AlertTriangle,
  hibernating: Moon,
  lost: UserX
};

export default function SegmentsTab({ 
  segmentation, 
  onRunAutoTagging,
  formatCurrency,
  formatPercent
}) {
  return (
    <div className="space-y-6" data-testid="segments-tab">
      {/* Auto-tagging Action */}
      <div className="bg-primary/5 dark:bg-primary/10 rounded-xl p-4 border border-primary/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h4 className="font-medium text-foreground">Customer Segmentation Automation</h4>
          <p className="text-sm text-muted-foreground">
            Run RFM analysis and auto-tag customers based on their behavior
          </p>
        </div>
        <Button onClick={onRunAutoTagging} className="gap-2 shrink-0">
          <Zap className="w-4 h-4" />
          Run Auto-Tagging
        </Button>
      </div>

      {/* Segment Cards */}
      {segmentation && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {segmentation.segment_summaries?.map((seg) => {
            const Icon = SEGMENT_ICONS[seg.segment_id] || Users;
            return (
              <div 
                key={seg.segment_id}
                className="bg-card rounded-xl p-5 border border-border hover:border-primary/50 transition-colors"
                data-testid={`segment-card-${seg.segment_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${seg.color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: seg.color }} />
                  </div>
                  <span 
                    className="text-2xl font-bold"
                    style={{ color: seg.color }}
                  >
                    {seg.customer_count}
                  </span>
                </div>
                <h4 className="font-medium text-foreground mb-1">{seg.name}</h4>
                <p className="text-sm text-muted-foreground mb-3">{seg.description}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-muted/50 dark:bg-muted/30 rounded-lg p-2">
                    <p className="text-muted-foreground text-xs">Revenue</p>
                    <p className="font-medium text-foreground">{formatCurrency(seg.total_revenue)}</p>
                  </div>
                  <div className="bg-muted/50 dark:bg-muted/30 rounded-lg p-2">
                    <p className="text-muted-foreground text-xs">Avg Value</p>
                    <p className="font-medium text-foreground">{formatCurrency(seg.avg_revenue_per_customer)}</p>
                  </div>
                </div>
                {seg.recommended_actions && seg.recommended_actions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Recommended Actions:</p>
                    <div className="flex flex-wrap gap-1">
                      {seg.recommended_actions.slice(0, 2).map((action, i) => (
                        <span 
                          key={i}
                          className="text-xs px-2 py-1 rounded-full bg-muted dark:bg-muted/50 text-muted-foreground"
                        >
                          {action}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Segment Customer List */}
      {segmentation?.customers && segmentation.customers.length > 0 && (
        <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4">All Segmented Customers</h3>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Customer</th>
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Segment</th>
                  <th className="text-right py-3 px-2 font-medium text-muted-foreground">Orders</th>
                  <th className="text-right py-3 px-2 font-medium text-muted-foreground">Last Order</th>
                  <th className="text-right py-3 px-2 font-medium text-muted-foreground">Total Spent</th>
                  <th className="text-center py-3 px-2 font-medium text-muted-foreground">RFM Score</th>
                </tr>
              </thead>
              <tbody>
                {segmentation.customers.slice(0, 20).map((customer) => {
                  const Icon = SEGMENT_ICONS[customer.segment] || Users;
                  return (
                    <tr 
                      key={customer.customer_id} 
                      className="border-b border-border/50 hover:bg-muted/30 dark:hover:bg-muted/20"
                    >
                      <td className="py-3 px-2">
                        <div>
                          <p className="font-medium text-foreground">{customer.name}</p>
                          <p className="text-xs text-muted-foreground">{customer.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <Icon 
                            className="w-4 h-4 shrink-0" 
                            style={{ color: customer.segment_color }} 
                          />
                          <span className="truncate" style={{ color: customer.segment_color }}>
                            {customer.segment_name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-right text-foreground">{customer.frequency}</td>
                      <td className="py-3 px-2 text-right text-foreground">{customer.recency_days}d ago</td>
                      <td className="py-3 px-2 text-right font-medium text-foreground">
                        {formatCurrency(customer.monetary)}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="inline-flex items-center justify-center px-2 py-1 rounded bg-muted dark:bg-muted/50 text-xs font-mono text-foreground">
                          R{customer.rfm_scores?.r} F{customer.rfm_scores?.f} M{customer.rfm_scores?.m}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
